import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Clock, Rocket, ShieldCheck } from "lucide-react";

const STATUS = {
  ga: { label: "Generally available", cls: "bg-emerald-500/10 text-emerald-700" },
  beta: { label: "Beta", cls: "bg-blue-500/10 text-blue-700" },
  planned: { label: "Planned", cls: "bg-muted text-muted-foreground" },
};

export default function Roadmap() {
  const [modules, setModules] = useState([]);
  useEffect(() => { api.get("/modules").then(({ data }) => setModules(data)); }, []);

  const grouped = {
    beta: modules.filter(m => m.status === "beta"),
    ga: modules.filter(m => m.status === "ga"),
    planned: modules.filter(m => m.status === "planned"),
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.18em] text-accent font-bold">Platform</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">Roadmap</h1>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
            CA Ritika Singh is being built as a full tax practice platform — Income Tax, TDS, GST, MCA, ERI, DSC. Everything sits on a shared core (auth, documents, notifications, audit, calculation engine, compliance gateway).
          </p>
        </div>

        {[
          { key: "beta", title: "In beta today", icon: Rocket },
          { key: "ga", title: "Live", icon: CheckCircle2 },
          { key: "planned", title: "Planned modules", icon: Clock },
        ].map(g => (
          <section key={g.key} className="mt-12">
            <div className="flex items-center gap-2 mb-4">
              <g.icon className="h-4 w-4 text-primary" />
              <h2 className="font-display text-xl font-semibold">{g.title}</h2>
              <span className="text-xs text-muted-foreground">· {grouped[g.key].length}</span>
            </div>
            {grouped[g.key].length === 0 && <div className="text-sm text-muted-foreground">Nothing here yet.</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {grouped[g.key].map(m => (
                <Card key={m.key} className="p-5 border-border">
                  <div className="flex items-start justify-between gap-3">
                    <div className="font-display font-semibold">{m.name}</div>
                    <Badge variant="outline" className={`text-[10px] ${STATUS[m.status].cls}`}>{STATUS[m.status].label}</Badge>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{m.description}</p>
                  {m.roadmap?.length > 0 && (
                    <ul className="mt-3 space-y-1 text-xs">
                      {m.roadmap.map((r, i) => <li key={i} className="flex gap-1.5"><span className="text-muted-foreground">·</span> {r}</li>)}
                    </ul>
                  )}
                </Card>
              ))}
            </div>
          </section>
        ))}

        <Card className="mt-16 p-8 border-border">
          <ShieldCheck className="h-8 w-8 text-accent" />
          <h3 className="mt-3 font-display text-2xl font-semibold">Architecture, briefly</h3>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            Every module plugs into the same core: Authentication, Client Database, Document Repository, Notification Service, Workflow Engine, Immutable Audit Log, Calculation Engine, Compliance Gateway (Government Integration Layer), and Reporting Framework. Adding TDS or GST is scaffolding a new module — the core doesn't change.
          </p>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
